"use client";
import React, { useState, useEffect } from "react";
import { Menu, X, Moon, Sun, ChevronDown, Search } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useTheme } from "../context/ThemeContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Button } from "./ui/button";
import { getAccessTokenClient } from "@/src/lib/api/tokens.client";
import { decodeJwtPayload } from "@/src/lib/api/jwt";

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation("common");

  useEffect(() => {
    const token = getAccessTokenClient();
    setIsAuthenticated(!!(token && decodeJwtPayload(token)));
  }, []);

  const navLinks = [
    { path: "/products", label: t("nav.products"), hasDropdown: true },
    { path: "/diecuts", label: t("nav.diecuts"), hasDropdown: false },
    { path: "/industries", label: t("nav.industries"), hasDropdown: true },
    { path: "/about", label: t("nav.about"), hasDropdown: true },
    {
      path: "/sustainability",
      label: t("nav.sustainability"),
      hasDropdown: false,
    },
  ];

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-[12px] flex justify-center px-4 sm:px-6 lg:px-8 py-4">
        <nav className="backdrop-blur-[20px] bg-white/30 dark:bg-white/10 max-w-[1280px] w-full rounded-[33px] relative glass-shadow-outer">
          {/* Border overlay */}
          <div
            aria-hidden="true"
            className="absolute border border-white/5 border-solid inset-0 pointer-events-none rounded-[33px]"
          />

          {/* Content */}
          <div className="flex items-center justify-between px-6 py-4">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2 group z-10">
              <motion.div
                // whileHover={{ rotate: 360 }}
                // transition={{ duration: 0.6 }}
                className="w-8 h-8 rounded-lg bg-gradient-to-br from-walnut-600 to-walnut-800 dark:from-walnut-500 dark:to-walnut-700 flex items-center justify-center shadow-lg"
              >
                <span className="text-white font-bold text-lg">W</span>
              </motion.div>
              <div className="flex flex-col font-bold leading-tight">
                <span className="text-walnut-700 dark:text-walnut-300 text-xl">
                  WALNUT
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden xl:flex items-center space-x-1 flex-1 justify-center mx-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  href={link.path}
                  className="h-[40px] px-4 flex items-center justify-center gap-1 rounded-[33px] text-sm font-medium text-nav-link-text hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
                >
                  {link.label}
                  {link.hasDropdown && (
                    <ChevronDown className="w-3 h-3 opacity-80" />
                  )}
                </Link>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-2">
              {/* Icon Buttons - Desktop */}
              <div className="hidden md:flex items-center space-x-2">
                {/* Theme Toggle */}
                <Button
                  variant="glass"
                  size="icon"
                  pill
                  onClick={toggleTheme}
                  className="relative"
                  aria-label="Toggle theme"
                >
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                  />
                  <motion.div
                    initial={false}
                    animate={{ rotate: theme === "dark" ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {theme === "light" ? (
                      <Moon className="w-4 h-4 text-nav-icon" />
                    ) : (
                      <Sun className="w-4 h-4 text-yellow-400" />
                    )}
                  </motion.div>
                </Button>

                {/* Language Switcher */}
                <LanguageSwitcher />

                {/* Auth Buttons */}
                <div className="hidden xl:flex items-center gap-2">
                  {isAuthenticated ? (
                    <Link
                      href="/dashboard"
                      className="backdrop-blur-[5px] bg-walnut-500/20 dark:bg-walnut-400/20 h-[36px] px-[13px] flex items-center justify-center rounded-[33px] border border-walnut-500/30 dark:border-walnut-400/30 glass-shadow-outer relative hover:bg-walnut-500/30 transition-all duration-300"
                    >
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                      />
                      <span className="text-sm font-medium text-black dark:text-white relative z-10">
                        Dashboard
                      </span>
                    </Link>
                  ) : (
                    <>
                      {/* Sign In Button - Glass */}
                      <Link
                        href="/signin"
                        className="backdrop-blur-[5px] bg-white/10 dark:bg-white/5 h-[36px] px-[13px] flex items-center justify-center rounded-[33px] border border-white/20 dark:border-white/10 glass-shadow-outer relative hover:bg-white/20 transition-all duration-300"
                      >
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                        />
                        <span className="text-sm font-medium text-black dark:text-white relative z-10">
                          {t("common.signIn")}
                        </span>
                      </Link>

                      {/* Sign Up Button - Accent */}
                      <Link
                        href="/signup"
                        className="backdrop-blur-[5px] bg-walnut-500/20 dark:bg-walnut-400/20 h-[36px] px-[13px] flex items-center justify-center rounded-[33px] border border-walnut-500/30 dark:border-walnut-400/30 glass-shadow-outer relative hover:bg-walnut-500/30 transition-all duration-300"
                      >
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                        />
                        <span className="text-sm font-medium text-black dark:text-white relative z-10">
                          {t("common.signUp")}
                        </span>
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Mobile Menu Button */}
              <Button
                variant="glass"
                size="icon"
                pill
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="xl:hidden relative"
                aria-label="Toggle mobile menu"
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                />
                {isMobileMenuOpen ? (
                  <X className="w-5 h-5 text-nav-icon relative z-10" />
                ) : (
                  <Menu className="w-5 h-5 text-nav-icon relative z-10" />
                )}
              </Button>
            </div>
          </div>

          {/* Inset shadows for depth */}
          <div className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-nav-shell" />
        </nav>
      </div>

      {/* Mobile Menu - Outside navbar container for true fullscreen */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Fullscreen mobile menu */}
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden fixed inset-0 z-[101] backdrop-blur-[20px] bg-white/95 dark:bg-grey-950/95 overflow-y-auto"
            >
              <div className="min-h-screen p-6">
                {/* Header with close button */}
                <div className="flex items-center justify-between mb-12">
                  <Link
                    href="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3"
                  >
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 dark:from-walnut-600 dark:to-walnut-800 flex items-center justify-center">
                      <span className="text-white text-xl font-bold">W</span>
                    </div>
                    <span className="text-xl font-bold text-walnut-900 dark:text-white">
                      Walnut
                    </span>
                  </Link>

                  <Button
                    variant="glass"
                    size="icon"
                    pill
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="relative h-[44px] w-[44px]"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6 relative z-10 text-grey-800 dark:text-grey-200" />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
                    />
                  </Button>
                </div>

                {/* Navigation links */}
                <div className="space-y-2 mb-8">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        href={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-6 py-4 rounded-[20px] text-lg font-medium text-nav-link-text hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300 backdrop-blur-xl bg-white/30 dark:bg-white/5 border border-white/20 dark:border-white/10 glass-shadow-mobile-link"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Action buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3 pt-6 border-t border-white/20 dark:border-white/10"
                >
                  {isAuthenticated ? (
                    <Link
                      href="/dashboard"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full px-6 py-4 rounded-[33px] text-base font-semibold text-center bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300"
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link
                        href="/signin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full px-6 py-4 rounded-[33px] text-base font-semibold text-center backdrop-blur-xl bg-white/40 dark:bg-white/10 hover:bg-white/60 dark:hover:bg-white/20 transition-all duration-300 text-walnut-900 dark:text-white border border-white/20 dark:border-white/10 glass-shadow-mobile-link"
                      >
                        {t("common.signIn")}
                      </Link>
                      <Link
                        href="/signup"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full px-6 py-4 rounded-[33px] text-base font-semibold text-center bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300"
                      >
                        {t("common.signUp")}
                      </Link>
                    </>
                  )}
                </motion.div>

                {/* Language Switcher - Mobile */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  className="mt-4"
                >
                  <LanguageSwitcher />
                </motion.div>

                {/* Theme toggle */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 pt-6 border-t border-white/20 dark:border-white/10"
                >
                  <Button
                    variant="glass"
                    onClick={toggleTheme}
                    className="w-full flex items-center justify-between px-6 py-4 rounded-[20px] backdrop-blur-xl bg-white/30 dark:bg-white/5 text-walnut-900 dark:text-white hover:bg-white/40 dark:hover:bg-white/10"
                  >
                    <span className="text-base font-medium">
                      {t("common.theme")}
                    </span>
                    <div className="flex items-center gap-2">
                      {theme === "dark" ? (
                        <>
                          <Moon className="w-5 h-5" />
                          <span className="text-sm">{t("common.dark")}</span>
                        </>
                      ) : (
                        <>
                          <Sun className="w-5 h-5" />
                          <span className="text-sm">{t("common.light")}</span>
                        </>
                      )}
                    </div>
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
